import React, { useState, useEffect, useCallback } from 'react';
import { authHeaders } from '../authToken';
import { THEME_A } from '../themeAConfig';
import { fetchPlans, formatPlan, startCheckout } from '../checkout';
import '../teacher.css';

// Banner nudging a verified teacher (without an active teacher subscription) to buy
// the Teacher plan, which covers every student in their classes. Shows a real
// checkout button only when Stripe is configured and a 'teacher'-audience plan
// exists; otherwise it stays hidden (so production is unaffected until Stripe is live).
function TeacherUpgradeBanner({ playerData }) {
  const [plan, setPlan]   = useState(null);   // the teacher-audience plan, or null
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const isTeacher   = !!playerData?.is_teacher;
  const verified    = !!playerData?.teacher_verified;
  const onTeacherSub = playerData?.subscription_status === 'premium'
                       && playerData?.subscription_plan === 'teacher';
  const show = isTeacher && verified && !onTeacherSub;

  useEffect(() => {
    if (!show) return;
    let alive = true;
    fetchPlans().then(plans => {
      if (alive) setPlan((plans || []).find(p => p.audience === 'teacher') || null);
    });
    return () => { alive = false; };
  }, [show]);

  if (!show || !plan) return null;

  const buy = async () => {
    setBusy(true); setError('');
    const r = await startCheckout(plan.price_id);
    if (!r.ok) { setError(r.error); setBusy(false); }
  };

  return (
    <div className="td-upgrade" role="region" aria-label="Teacher plan">
      <div className="td-upgrade-text">
        <strong>Cover your whole class.</strong> The Teacher plan unlocks Premium for every
        student you enrol, for {formatPlan(plan)}. Access ends for your students if the
        subscription lapses.
      </div>
      <button className="td-upgrade-btn" onClick={buy} disabled={busy}>
        {busy ? 'Starting…' : `Upgrade — ${formatPlan(plan)}`}
      </button>
      {error && <div className="td-upgrade-err">{error}</div>}
    </div>
  );
}

// Flat map of statement scope (e.g. "A1.1.3") -> its title, for the Focus list.
const STATEMENT_TITLE = {};
THEME_A.forEach(t => (t.subtopics || []).forEach(st => (st.statements || []).forEach(s => {
  STATEMENT_TITLE[s.scope] = s.title;
})));

/*
 * Teacher dashboard — a separate screen (route /teacher), reached from the
 * hamburger menu and shown only to teacher accounts. Teachers manage their own
 * classes and rosters here; it does not replace the normal learner experience.
 *
 * All calls hit /api/teacher/* with the caller's own bearer token (authHeaders);
 * the backend gates on is_teacher and class ownership. See
 * ENROLLMENT_ARCHITECTURE.md / PHASE1_ENROLLMENT_PLAN.md.
 */

const CONFLICT_TEXT = {
  other_class: 'already in another class',
  personal:    'a personal paid account (ask them to use a school email)',
  is_teacher:  'a teacher account',
};

function pctLabel(v) { return v == null ? '—' : Math.round(v * 100) + '%'; }

function StudentDetail({ detail }) {
  const c = detail.coding, t = detail.theory;
  const hasStuck = c.stuck.length > 0 || t.weak.length > 0;
  return (
    <div className="td-sd">
      <div className="td-sd-summary">
        <strong>{detail.name || detail.email}</strong>
        <span className="td-muted">Coding {c.passed}/{c.total} · Theory {pctLabel(t.overall)}</span>
      </div>

      <section className="td-sd-sec">
        <h3 className="td-focus-h">Stuck points</h3>
        {!hasStuck ? (
          <p className="td-muted">Nothing flagged — on track so far.</p>
        ) : (
          <>
            {c.stuck.length > 0 && (
              <>
                <div className="td-sd-sub">Coding</div>
                <ul className="td-focus-list">
                  {c.stuck.map(s => (
                    <li key={s.ref + s.title} className="td-focus-item">
                      <span className="td-focus-main"><span className="ib-mono td-focus-ref">{s.ref}</span> {s.title}</span>
                      <span className="td-focus-metric"><span className="td-focus-badge">not passed</span>{s.attempts} attempts · {s.fails} fails in a row</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {t.weak.length > 0 && (
              <>
                <div className="td-sd-sub">Theory</div>
                <ul className="td-focus-list">
                  {t.weak.map(w => (
                    <li key={w.statement_id} className="td-focus-item">
                      <span className="td-focus-main"><span className="ib-mono td-focus-ref">{w.statement_id}</span> {STATEMENT_TITLE[w.statement_id] || ''}</span>
                      <span className="td-focus-metric"><span className="td-focus-badge">{pctLabel(w.accuracy)}</span>{w.attempts} attempts</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </section>

      <section className="td-sd-sec">
        <h3 className="td-focus-h">Coding by unit</h3>
        <div className="td-sd-bars">
          {c.units.map(u => (
            <div key={u.ref} className="td-sd-bar">
              <span className="td-sd-bar-label ib-mono">{u.ref}</span>
              <span className="td-sd-bar-track"><span className="td-sd-bar-fill" style={{ width: (u.total ? Math.round(u.passed / u.total * 100) : 0) + '%' }} /></span>
              <span className="td-sd-bar-num">{u.passed}/{u.total}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="td-sd-sec">
        <h3 className="td-focus-h">Theory by topic</h3>
        <div className="td-sd-bars">
          {t.topics.map(tp => (
            <div key={tp.topic} className="td-sd-bar">
              <span className="td-sd-bar-label ib-mono">{tp.topic}</span>
              <span className="td-sd-bar-track"><span className="td-sd-bar-fill td-sd-bar-fill-theory" style={{ width: (tp.percent != null ? Math.round(tp.percent * 100) : 0) + '%' }} /></span>
              <span className="td-sd-bar-num">{pctLabel(tp.percent)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatusPill({ status }) {
  const active = status === 'active';
  return (
    <span className={`td-pill ${active ? 'td-pill-active' : 'td-pill-pending'}`}>
      {active ? 'Active' : 'Invited'}
    </span>
  );
}

export default function TeacherDashboard({ onBack, playerData }) {
  const [classes, setClasses]       = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [roster, setRoster]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const [creating, setCreating]     = useState(false);
  const [newName, setNewName]       = useState('');

  const [renaming, setRenaming]     = useState(false);
  const [renameVal, setRenameVal]   = useState('');

  const [view, setView]             = useState('roster');   // 'roster' | 'progress' | 'theory'
  const [grid, setGrid]             = useState(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [theory, setTheory]         = useState(null);
  const [theoryLoading, setTheoryLoading] = useState(false);
  const [focus, setFocus]           = useState(null);
  const [focusLoading, setFocusLoading] = useState(false);
  const [openStudent, setOpenStudent] = useState(null);   // user_id of drilled-in student
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [paste, setPaste]           = useState('');
  const [busy, setBusy]             = useState(false);
  const [summary, setSummary]       = useState(null);
  const [notice, setNotice]         = useState('');

  const selected = classes.find(c => c.class_id === selectedId) || null;

  const loadClasses = useCallback(async () => {
    setError('');
    try {
      const r = await fetch('/api/teacher/classes', { headers: authHeaders() });
      if (!r.ok) throw new Error('Could not load your classes.');
      const data = await r.json();
      setClasses(data.classes || []);
      setSelectedId(prev => prev || (data.classes && data.classes[0] ? data.classes[0].class_id : null));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRoster = useCallback(async (classId) => {
    if (!classId) { setRoster([]); return; }
    try {
      const r = await fetch(`/api/teacher/classes/${classId}/roster`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Could not load the roster.');
      const data = await r.json();
      setRoster(data.roster || []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { loadClasses(); }, [loadClasses]);
  const loadGrid = useCallback(async (classId) => {
    if (!classId) { setGrid(null); return; }
    setGridLoading(true);
    try {
      const r = await fetch(`/api/teacher/classes/${classId}/analytics/grid`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Could not load progress.');
      setGrid(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setGridLoading(false);
    }
  }, []);

  useEffect(() => { setSummary(null); setNotice(''); setRenaming(false); setView('roster'); setOpenStudent(null); loadRoster(selectedId); }, [selectedId, loadRoster]);
  const loadTheory = useCallback(async (classId) => {
    if (!classId) { setTheory(null); return; }
    setTheoryLoading(true);
    try {
      const r = await fetch(`/api/teacher/classes/${classId}/analytics/theory`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Could not load theory progress.');
      setTheory(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setTheoryLoading(false);
    }
  }, []);

  useEffect(() => { if (view === 'progress') loadGrid(selectedId); }, [view, selectedId, loadGrid]);
  const loadFocus = useCallback(async (classId) => {
    if (!classId) { setFocus(null); return; }
    setFocusLoading(true);
    try {
      const r = await fetch(`/api/teacher/classes/${classId}/analytics/focus`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Could not load focus data.');
      setFocus(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setFocusLoading(false);
    }
  }, []);

  useEffect(() => { if (view === 'theory') loadTheory(selectedId); }, [view, selectedId, loadTheory]);
  useEffect(() => { if (view === 'focus') loadFocus(selectedId); }, [view, selectedId, loadFocus]);

  const showStudent = useCallback(async (userId) => {
    setOpenStudent(userId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await fetch(`/api/teacher/classes/${selectedId}/student/${userId}`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Could not load that student.');
      setDetail(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setDetailLoading(false);
    }
  }, [selectedId]);

  async function setClassSetting(field, value) {
    try {
      const r = await fetch(`/api/teacher/classes/${selectedId}`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ [field]: value }),
      });
      if (!r.ok) throw new Error('Could not update class settings.');
      await loadClasses();
      if (view === 'progress') loadGrid(selectedId);
      else if (view === 'theory') loadTheory(selectedId);
      else if (view === 'focus') loadFocus(selectedId);
    } catch (e) {
      setError(e.message);
    }
  }

  async function renameClass(e) {
    e.preventDefault();
    const name = renameVal.trim();
    if (!name || !selectedId) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/teacher/classes/${selectedId}`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ name }),
      });
      if (!r.ok) throw new Error('Could not rename the class.');
      setRenaming(false);
      await loadClasses();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function createClass(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const r = await fetch('/api/teacher/classes', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ name }),
      });
      if (!r.ok) throw new Error('Could not create the class.');
      const created = await r.json();
      setNewName('');
      setCreating(false);
      await loadClasses();
      setSelectedId(created.class_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function addStudents(e) {
    e.preventDefault();
    if (!selectedId || !paste.trim()) return;
    setBusy(true);
    setSummary(null);
    try {
      const r = await fetch(`/api/teacher/classes/${selectedId}/roster`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ emails: paste }),
      });
      if (!r.ok) throw new Error('Could not add students.');
      const data = await r.json();
      setSummary(data);
      setPaste('');
      await loadClasses();
      await loadRoster(selectedId);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(email) {
    if (!window.confirm(`Remove ${email} from this class? Their account and progress are kept.`)) return;
    try {
      const r = await fetch(`/api/teacher/classes/${selectedId}/roster/${encodeURIComponent(email)}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) throw new Error('Could not remove that student.');
      await loadClasses();
      await loadRoster(selectedId);
    } catch (err) {
      setError(err.message);
    }
  }

  async function moveMember(email, toClassId) {
    if (!toClassId) return;
    try {
      const r = await fetch(`/api/teacher/classes/${selectedId}/roster/${encodeURIComponent(email)}/move`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ to_class_id: toClassId }),
      });
      if (!r.ok) throw new Error('Could not move that student.');
      await loadClasses();
      await loadRoster(selectedId);
    } catch (e) {
      setError(e.message);
    }
  }

  async function resend(email) {
    setNotice('');
    try {
      const r = await fetch(`/api/teacher/classes/${selectedId}/roster/${encodeURIComponent(email)}/resend`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!r.ok) throw new Error('Could not resend the invite.');
      setNotice(`Invite resent to ${email}.`);
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="td-root">
      <header className="td-topbar">
        <button className="td-back" onClick={onBack} aria-label="Back to app">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="10 3 5 8 10 13" />
          </svg>
          Back
        </button>
        <h1 className="td-title">My classes</h1>
      </header>

      {error && <div className="td-error" role="alert">{error} <button onClick={() => setError('')} aria-label="Dismiss">×</button></div>}

      <TeacherUpgradeBanner playerData={playerData} />

      <div className="td-layout">
        {/* Left: class list */}
        <aside className="td-sidebar">
          <div className="td-sidebar-head">
            <span>Classes</span>
            <button className="td-btn td-btn-small" onClick={() => setCreating(v => !v)}>+ New</button>
          </div>

          {creating && (
            <form className="td-newclass" onSubmit={createClass}>
              <input
                autoFocus
                className="td-input"
                placeholder="Class name (e.g. DP1 HL 2026)"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
              <div className="td-newclass-actions">
                <button className="td-btn td-btn-primary" disabled={busy || !newName.trim()}>Create</button>
                <button type="button" className="td-btn td-btn-ghost" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</button>
              </div>
            </form>
          )}

          {loading ? (
            <p className="td-muted">Loading…</p>
          ) : classes.length === 0 ? (
            <p className="td-muted">No classes yet. Create one to start enrolling students.</p>
          ) : (
            <ul className="td-classlist">
              {classes.map(c => (
                <li key={c.class_id}>
                  <button
                    className={`td-classitem ${c.class_id === selectedId ? 'td-classitem-active' : ''}`}
                    onClick={() => setSelectedId(c.class_id)}
                  >
                    <span className="td-classname">{c.name}</span>
                    <span className="td-classcount">
                      {c.registered_count}/{c.member_count}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Right: roster for the selected class */}
        <main className="td-main">
          {!selected ? (
            <div className="td-empty">Select or create a class.</div>
          ) : (
            <>
              <div className="td-main-head">
                {renaming ? (
                  <form className="td-rename" onSubmit={renameClass}>
                    <input
                      autoFocus
                      className="td-input"
                      value={renameVal}
                      onChange={e => setRenameVal(e.target.value)}
                    />
                    <button className="td-btn td-btn-primary td-btn-small" disabled={busy || !renameVal.trim()}>Save</button>
                    <button type="button" className="td-btn td-btn-ghost td-btn-small" onClick={() => setRenaming(false)}>Cancel</button>
                  </form>
                ) : (
                  <div className="td-head-name">
                    <h2>{selected.name}</h2>
                    <button className="td-btn td-btn-ghost td-btn-small" onClick={() => { setRenameVal(selected.name); setRenaming(true); }}>Rename</button>
                  </div>
                )}
                <span className="td-muted">
                  {selected.member_count} student{selected.member_count === 1 ? '' : 's'} · {selected.registered_count} active
                </span>
              </div>

              {openStudent ? (
                <div className="td-detail">
                  <button className="td-btn td-btn-ghost td-btn-small td-detail-back" onClick={() => setOpenStudent(null)}>‹ Back to class</button>
                  {detailLoading || !detail ? <p className="td-muted">Loading…</p> : <StudentDetail detail={detail} />}
                </div>
              ) : (
              <>
              <div className="td-settings">
                <span className="td-settings-group">
                  <span className="td-settings-label">Language</span>
                  <button className={`td-seg ${selected.language !== 'java' ? 'td-seg-on' : ''}`} onClick={() => setClassSetting('language', 'python')}>Python</button>
                  <button className={`td-seg ${selected.language === 'java' ? 'td-seg-on' : ''}`} onClick={() => setClassSetting('language', 'java')}>Java</button>
                </span>
                <span className="td-settings-group">
                  <span className="td-settings-label">Level</span>
                  <button className={`td-seg ${selected.level === 'sl' ? 'td-seg-on' : ''}`} onClick={() => setClassSetting('level', 'sl')}>SL</button>
                  <button className={`td-seg ${selected.level !== 'sl' ? 'td-seg-on' : ''}`} onClick={() => setClassSetting('level', 'hl')}>HL</button>
                </span>
              </div>
              <div className="td-tabs">
                <button className={`td-tab ${view === 'roster' ? 'td-tab-active' : ''}`} onClick={() => setView('roster')}>Roster</button>
                <button className={`td-tab ${view === 'progress' ? 'td-tab-active' : ''}`} onClick={() => setView('progress')}>Coding</button>
                <button className={`td-tab ${view === 'theory' ? 'td-tab-active' : ''}`} onClick={() => setView('theory')}>Theory</button>
                <button className={`td-tab ${view === 'focus' ? 'td-tab-active' : ''}`} onClick={() => setView('focus')}>Focus</button>
              </div>

              {view === 'roster' && (<>
              <form className="td-add" onSubmit={addStudents}>
                <label className="td-add-label" htmlFor="td-paste">Add students</label>
                <textarea
                  id="td-paste"
                  className="td-textarea"
                  placeholder={"Paste student emails, one per line or comma-separated\nalice@school.edu\nbob@school.edu"}
                  value={paste}
                  onChange={e => setPaste(e.target.value)}
                  rows={4}
                />
                <button className="td-btn td-btn-primary" disabled={busy || !paste.trim()}>
                  {busy ? 'Working…' : 'Add students'}
                </button>
              </form>

              {summary && (
                <div className="td-summary" role="status">
                  <strong>
                    {summary.invited.length} invited, {summary.adopted.length} added
                    {summary.skipped.length ? `, ${summary.skipped.length} already in class` : ''}
                    {summary.conflicts.length ? `, ${summary.conflicts.length} not added` : ''}.
                  </strong>
                  {summary.conflicts.length > 0 && (
                    <ul className="td-conflicts">
                      {summary.conflicts.map(c => (
                        <li key={c.email}>
                          <span className="ib-mono">{c.email}</span> — {CONFLICT_TEXT[c.reason] || c.reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {notice && <div className="td-notice" role="status">{notice}</div>}

              <div className="td-roster">
                {roster.length === 0 ? (
                  <p className="td-muted">No students yet. Paste a class list above.</p>
                ) : (
                  <table className="td-table">
                    <thead>
                      <tr><th>Email</th><th>Status</th><th className="td-actions-col">Actions</th></tr>
                    </thead>
                    <tbody>
                      {roster.map(r => (
                        <tr key={r.email}>
                          <td className="ib-mono td-email">{r.status === 'active' && r.student_user_id
                            ? <button className="td-linkbtn" onClick={() => showStudent(r.student_user_id)}>{r.email}</button>
                            : r.email}</td>
                          <td><StatusPill status={r.status} /></td>
                          <td className="td-actions-col">
                            {r.status === 'invited' && (
                              <button className="td-btn td-btn-ghost td-btn-small" onClick={() => resend(r.email)}>Resend</button>
                            )}
                            {classes.length > 1 && (
                              <select
                                className="td-move"
                                value=""
                                aria-label={`Move ${r.email} to another class`}
                                onChange={e => { const v = e.target.value; e.target.value = ''; moveMember(r.email, v); }}
                              >
                                <option value="">Move…</option>
                                {classes.filter(c => c.class_id !== selectedId).map(c => (
                                  <option key={c.class_id} value={c.class_id}>{c.name}</option>
                                ))}
                              </select>
                            )}
                            <button className="td-btn td-btn-danger td-btn-small" onClick={() => removeMember(r.email)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              </>)}

              {view === 'progress' && (
                <div className="td-grid-wrap">
                  {gridLoading ? (
                    <p className="td-muted">Loading…</p>
                  ) : !grid || grid.rows.length === 0 ? (
                    <p className="td-muted">No registered students yet — progress shows once students sign in and start coding.</p>
                  ) : (
                    <table className="td-grid">
                      <thead>
                        <tr>
                          <th className="td-grid-namehead">Student</th>
                          {grid.columns.map(c => <th key={c.unit_id} title={`${c.total} exercises in this unit`}>{c.ref} <span className="td-grid-den">({c.total})</span></th>)}
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grid.rows.map(r => (
                          <tr key={r.email}>
                            <td className="td-grid-name"><button className="td-linkbtn" onClick={() => showStudent(r.user_id)} title={r.email}>{r.name || r.email}</button></td>
                            {grid.columns.map(c => {
                              const p = r.cells[c.unit_id] || 0;
                              const ratio = c.total ? p / c.total : 0;
                              return (
                                <td key={c.unit_id} className="td-grid-cell"
                                    style={p ? { background: `rgba(47,158,106,${(0.18 + ratio * 0.62).toFixed(2)})` } : undefined}
                                    title={`${p}/${c.total}`}>{p}</td>
                              );
                            })}
                            <td className="td-grid-total">{r.passed}/{r.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {grid && grid.rows.length > 0 && (
                    <p className="td-grid-key">Numbers are main exercises passed. The count in each column header, e.g. <strong>B2.1 (19)</strong>, is how many exercises that unit has, so a full column means everything done.</p>
                  )}
                </div>
              )}

              {view === 'theory' && (
                <div className="td-grid-wrap">
                  {theoryLoading ? (
                    <p className="td-muted">Loading…</p>
                  ) : !theory || theory.rows.length === 0 ? (
                    <p className="td-muted">No registered students yet — theory mastery shows once students take quizzes.</p>
                  ) : (
                    <table className="td-grid">
                      <thead>
                        <tr>
                          <th className="td-grid-namehead">Student</th>
                          {theory.columns.map(c => <th key={c.topic_id} title={`${c.total} statements`}>{c.topic_id}</th>)}
                          <th>Overall</th>
                        </tr>
                      </thead>
                      <tbody>
                        {theory.rows.map(r => (
                          <tr key={r.email}>
                            <td className="td-grid-name"><button className="td-linkbtn" onClick={() => showStudent(r.user_id)} title={r.email}>{r.name || r.email}</button></td>
                            {theory.columns.map(c => {
                              const cell = r.cells[c.topic_id] || {};
                              const v = cell.percent;
                              return (
                                <td key={c.topic_id} className="td-grid-cell"
                                    style={v != null ? { background: `rgba(115,104,212,${(0.15 + v * 0.6).toFixed(2)})` } : undefined}
                                    title={`${cell.attempted || 0}/${cell.total || 0} statements attempted`}>
                                  {v == null ? '—' : Math.round(v * 100) + '%'}
                                </td>
                              );
                            })}
                            <td className="td-grid-total">{r.overall == null ? '—' : Math.round(r.overall * 100) + '%'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {theory && theory.rows.length > 0 && (
                    <p className="td-grid-key">Each percentage is a student’s average quiz accuracy across the statements they’ve attempted in that topic. It reflects how well they know it, not how much they’ve covered. A dash means not attempted yet.</p>
                  )}
                </div>
              )}

              {view === 'focus' && (
                <div className="td-focus">
                  {focusLoading || !focus ? (
                    <p className="td-muted">Loading…</p>
                  ) : (focus.coding.length === 0 && focus.theory.length === 0) ? (
                    <p className="td-muted">Nothing to flag yet. Trouble spots appear once at least {focus.min_students || 2} students have attempted the same exercises or quiz statements.</p>
                  ) : (
                    <>
                      <p className="td-grid-key">The exercises and statements the class is scoring below 70% on, hardest first, for spotting what to reteach. Only items at least {focus.min_students || 2} students have attempted are shown.</p>
                      <section className="td-focus-sec">
                        <h3 className="td-focus-h">Coding</h3>
                        {focus.coding.length === 0 ? <p className="td-muted">Nothing flagged.</p> : (
                          <ul className="td-focus-list">
                            {focus.coding.map(x => (
                              <li key={x.id} className="td-focus-item">
                                <span className="td-focus-main"><span className="ib-mono td-focus-ref">{x.ref}</span> {x.title}</span>
                                <span className="td-focus-metric">
                                  <span className="td-focus-badge">{Math.round(x.pass_rate * 100)}% passing</span>
                                  {x.attempted} tried · avg {x.avg_attempts} attempts
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                      <section className="td-focus-sec">
                        <h3 className="td-focus-h">Theory</h3>
                        {focus.theory.length === 0 ? <p className="td-muted">Nothing flagged.</p> : (
                          <ul className="td-focus-list">
                            {focus.theory.map(x => (
                              <li key={x.statement_id} className="td-focus-item">
                                <span className="td-focus-main"><span className="ib-mono td-focus-ref">{x.statement_id}</span> {STATEMENT_TITLE[x.statement_id] || ''}</span>
                                <span className="td-focus-metric">
                                  <span className="td-focus-badge">{Math.round(x.accuracy * 100)}% accuracy</span>
                                  {x.students} students · {x.attempts} attempts
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    </>
                  )}
                </div>
              )}
              </>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
